import type React from "react";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import CloseIcon from "@mui/icons-material/Close";
import Typography from "@mui/material/Typography";
import appIcon from "../../assets/app-icon.png";
import {colors} from "../../theme/tokens";

type AuthCardProps = {
    title: string;
    explanation?: React.ReactNode;
    children?: React.ReactNode;
    "data-testid"?: string;
};

/** The centred card for screens shown outside the signed-in shell: sign-in and startup failures. */
export function AuthCard({title, explanation, children, "data-testid": testId}: AuthCardProps) {
    return (
            <Box
                    data-testid={testId}
                    sx={{minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 3, backgroundColor: colors.loginCanvas}}
            >
                <Paper variant="outlined" sx={{width: "100%", maxWidth: 400, px: 4, py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2}}>
                    <Box component="img" src={appIcon} alt="" sx={{width: 44, height: 44, borderRadius: "10px"}} />
                    <Box sx={{textAlign: "center"}}>
                        <Typography variant="h5" component="h1">{title}</Typography>
                        {explanation && (
                                <Typography variant="body1" sx={{color: colors.muted, mt: 0.75}}>{explanation}</Typography>
                        )}
                    </Box>
                    <Box sx={{width: "100%", display: "flex", flexDirection: "column", gap: 2}}>{children}</Box>
                </Paper>
            </Box>
    );
}

/** Selectable error text for a support request, in a red panel. */
type ErrorDetailsProps = {
    title: string;
    details: string;
    /** Dismissal is offered only with a label for the close button. */
    close?: {label: string; onClose: () => void};
    "data-testid"?: string;
};

export function ErrorDetails({title, details, close, "data-testid": testId}: ErrorDetailsProps) {
    return (
            <Box
                    role="alert"
                    data-testid={testId}
                    sx={{position: "relative", p: 1.5, pr: close ? 5 : 1.5, borderRadius: "4px", border: `1px solid ${colors.errorPanelBorder}`, backgroundColor: colors.errorPanelBackground, color: colors.errorPanelText}}
            >
                <Typography sx={{fontSize: "13px", fontWeight: 500, mb: 0.75}}>{title}</Typography>
                <Box component="pre" sx={{m: 0, fontFamily: "ui-monospace, Menlo, monospace", fontSize: "12px", whiteSpace: "pre-wrap", wordBreak: "break-word", userSelect: "all"}}>
                    {details}
                </Box>
                {close && (
                        <IconButton
                                size="small"
                                aria-label={close.label}
                                onClick={close.onClose}
                                sx={{position: "absolute", top: 6, right: 6, color: "inherit"}}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                )}
            </Box>
    );
}
